import { IMedia } from '@/types'
import cx from 'classnames'
import Image from 'next/image'
import { PropsWithClassName } from 'types'
import { getImageUrl } from 'utils/image'

type ImageAspectRatio = '4:3' | '2:1' | '1:1' | '16:9'

interface ArticleHeaderProps {
  title?: string
  subheading?: string
  quote?: string
  category?: string
  image: IMedia
  imageAspectRatio?: ImageAspectRatio
  backgroundColor?: string
  className?: string
  /**
   * By default, the header content will be displayed on a column. The same behaviour if this field has the value `column`.
   *
   * In case of some templates, the content should be displayed on column on mobile and on row on desktop (lg). If this field has the value `lg:row`, the `imageAspectRatio` will be ignored on `screen sizes >= lg` and 1:1 will be enforced.
   */
  display?: 'column' | 'lg:row'
}

const getImageAspectRatio = (aspectRatio: string) => {
  switch (aspectRatio) {
    case '4:3':
      return '75%'
    case '2:1':
      return '50%'
    case '16:9':
      return '56.25%'
    default:
      return '100%'
  }
}

export const ArticleHeader = ({
  title,
  subheading,
  quote,
  category,
  image,
  imageAspectRatio = '4:3',
  backgroundColor,
  className,
  display = 'column',
}: ArticleHeaderProps) => {
  const imageAlt = image?.attributes?.name || ''
  const imageMediumSrc = getImageUrl(image, 'medium')
  const imageIntristicSrc = image?.attributes.url || ''

  return (
    <header
      className={cx(display === 'lg:row' && 'lg:!bg-warm-50')}
      style={{
        backgroundColor: backgroundColor || 'transparent',
      }}
    >
      <div
        className={cx(
          'flex w-full gap-8 px-6 sm:px-12 max-w-[96rem] mx-auto',
          display === 'column' && 'flex-col',
          display === 'lg:row' &&
            'flex-col lg:grid lg:grid-cols-2 lg:py-8 items-center xl:gap-16',
          className
        )}
      >
        {(category || title || subheading || quote) && (
          <div className='mx-auto mt-8 flex max-w-[800px] flex-col items-center gap-3'>
            {category && (
              <span className='w-fit rounded-full border border-cool-800 px-2.5 py-1 font-body text-sm font-medium'>
                {category}
              </span>
            )}

            {title && (
              <h1 className='text-center font-body text-2xl font-normal text-cool-900 md:text-3xl'>
                {title}
              </h1>
            )}

            {subheading && (
              <p className='text-body text-center font-body text-cool-700'>
                {subheading}
              </p>
            )}

            {quote && (
              <p className='text-body text-center font-body italic text-cool-700'>
                &quot;{quote}&quot;
              </p>
            )}
          </div>
        )}

        {display === 'column' && (
          <ArticleImage
            image={{ alt: imageAlt, url: imageMediumSrc }}
            imageAspectRatio={imageAspectRatio}
          />
        )}

        {display === 'lg:row' && (
          <>
            <ArticleImage
              image={{ alt: imageAlt, url: imageMediumSrc }}
              imageAspectRatio={imageAspectRatio}
              className='lg:hidden'
            />
            <div>
              <ArticleImage
                image={{ alt: imageAlt, url: imageIntristicSrc }}
                imageAspectRatio='1:1'
                className='hidden lg:block lg:w-auto lg:max-w-[640px]'
              />
            </div>
          </>
        )}
      </div>
    </header>
  )
}

interface ArticleImageProps extends PropsWithClassName {
  imageAspectRatio: ImageAspectRatio
  image: {
    url: string
    alt: string
  }
}

function ArticleImage({
  className,
  image,
  imageAspectRatio,
}: ArticleImageProps) {
  return (
    <div
      className={cx(
        'mx-auto w-full max-w-[800px]',
        {
          'w-full sm:w-[450px]': imageAspectRatio === '1:1',
          '!max-w-[1440px]': imageAspectRatio === '16:9',
        },
        className
      )}
    >
      <div
        className='relative h-0 w-full'
        style={{
          paddingBottom: getImageAspectRatio(imageAspectRatio),
        }}
      >
        <Image
          src={image.url}
          layout='fill'
          quality={85}
          alt={image.alt}
          className='rounded object-cover object-center'
          fetchPriority='high'
          loading='eager'
        />
      </div>
    </div>
  )
}
